import { BaseComponent } from '@shared/components/base.component';
import { CartService } from '../cart.service';
import { AppInjector } from '@shared/services/app-injector.service';
import { CartListData } from '@shared/interfaces/cart/cart-list-data.interface';
import { CartItem } from '@shared/interfaces/cart/cart-item.interface';
import { Donate } from '@shared/interfaces/cart/donate.interface';
import { EventEmitter, Output, Directive } from '@angular/core';
import { Response } from '@shared/models/response.model';
import { Project } from '@shared/models/project.model';
import { Config } from '@shared/config';
import { RoutesPath } from '@shared/routes-path';

@Directive()
export class BaseCartComponent extends BaseComponent {
  anonymous: boolean = false;
  cartService: CartService;
  cartList: CartListData;
  cartItems: CartItem[] = [];
  @Output() cartListLength: EventEmitter<number> = new EventEmitter();
  loading: boolean = false;

  constructor() {
    super();
    const injector = AppInjector.getInjector();
    this.cartService = injector.get(CartService);

    // BaseCartComponent is used in several files, and we need
    // to update all variables LIST. Use special eventMaker from the service and subscribe to this event.
    const subUpdateCart = this.cartService.callUpdateData.subscribe((el: CartListData) => {
      this.updateCartList(el);
    });
    this.someSubscriptions.add(subUpdateCart);
  }

  get payCount(): number {
    if (this.cartItems.length > 0) {
      let total = 0;
      for (const el of this.cartItems) {
        const amountNum = parseInt(el.amount, 10);
        if (amountNum === 0) {
          return 0;
        } else {
          total = total + amountNum;
        }
      }
      return total;
    } else {
      return 0;
    }
  }

  get disabledDonatedAllBtn() {
    return this.payCount < Config.MINIMAL_PAY_COUNT || this.payCount > Config.MAXIMAL_PAY_COUNT;
  }

  addItem(pack: Donate, goToPayment?: boolean) {
    this.loading = true;
    const subAddItem = this.cartService.addItem(pack).subscribe((el: Response<CartListData>) => {
      this.cartService.callUpdateData.emit(el.data);
      this.loading = false;
      for (const el2 of el.data.cart_items) {
        if (el2.project.id === pack.project_id) {
          if (+el2.amount !== pack.amount) {
            this.toast.warning(this.translate.instant('PROJECT.edited_to_cart'), null, {
              timeOut: 800,
            });
          } else {
            this.toast.success(this.translate.instant('PROJECT.added_to_cart'));
          }
          break;
        }
      }
      if (goToPayment) {
        this.router.navigate(['/' + RoutesPath.default.cart + '/' + RoutesPath.default.payment_tpay]);
      }
    }, this.onAddItemError.bind(this));
    this.someSubscriptions.add(subAddItem);
  }

  onAddItemError(error: Error) {
    this.loading = false;
    switch (error?.message) {
      case 'project_already_in_cart': {
        this.toast.error(
          this.translate.instant('CART.cart_error'),
          this.translate.instant('CART.cant_add_item_exist')
        );
        break;
      }
      case 'total_cart_value_is_to_big': {
        this.toast.error(
          this.translate.instant('CART.cart_error'),
          this.translate.instant('CART.total_cart_value_is_to_big') + Config.MAXIMAL_PAY_COUNT
        );
        break;
      }
      case 'amount_too_big': {
        this.toast.error(
          this.translate.instant('CART.cart_error'),
          this.translate.instant('CART.amount_too_big') + Config.MAXIMAL_PAY_COUNT
        );
        break;
      }
      default: {
        this.toast.error(
          this.translate.instant('CART.cart_error'),
          this.translate.instant('CART.cant_add_item')
        );
      }
    }
  }

  updateCartList(data: CartListData) {
    this.cartList = data;
    this.cartItems = data.cart_items;
    this.cartItems.map((el) => {
      el.mobileSwipeOpen = false;
      el.project = new Project(el.project);
    });
    this.cartListLength.emit(this.cartItems.length);

    // save to localStorage -> count of 'items' and 'total' price for payment method page
    const userSettings = this.storage.userSettings;
    const elems = this.cartItems.map((el: CartItem) => ({
      amount: el.amount,
      projectId: el.project.id,
    }));
    userSettings.cart = {
      elements: elems,
      items: this.cartItems.length,
      total: this.payCount,
    };
    this.storage.setUserSettings(userSettings);
  }

  getCartList(status?: boolean) {
    const subCartList = this.cartService.getCartList().subscribe((el: Response<CartListData>) => {
      this.cartService.callUpdateData.emit(el.data);
      if (el.data.cart_items.length > 0 && status) {
        this.router.url.search('cart') > 0
          ? document.body.classList.add('page__cart')
          : document.body.classList.remove('page__cart');
      }
      this.loading = false;
    }, this.onGetCartListError.bind(this));
    this.someSubscriptions.add(subCartList);
  }

  onGetCartListError(error: Error) {
    this.toast.error(
      this.translate.instant('CART.cart_error'),
      this.translate.instant('CART.cant_get_cart_list')
    );
  }

  removeItem(id) {
    const subRemoveItem = this.cartService
      .removeItem(id)
      .subscribe(
        (el: Response<CartListData>) => this.cartService.callUpdateData.emit(el.data),
        this.onRemoveItemCartError.bind(this)
      );
    this.someSubscriptions.add(subRemoveItem);
  }

  onRemoveItemCartError(error: Error) {
    this.toast.error(
      this.translate.instant('CART.cart_error'),
      this.translate.instant('CART.cant_remove_item')
    );
  }

  onClearCartError(error: Error) {
    this.toast.error(
      this.translate.instant('CART.cart_error'),
      this.translate.instant('CART.cant_clear_cart')
    );
  }

  allAnonymous() {
    this.anonymous = !this.anonymous;
  }

  onAllAnonymousError(error: Error) {
    this.toast.error(
      this.translate.instant('CART.cart_error'),
      this.translate.instant('CART.cant_all_anonymous')
    );
  }

  editAmount(pack: Donate) {
    const subEditAmount = this.cartService
      .changeAmount(pack)
      .subscribe(
        (el: Response<CartListData>) => this.cartService.callUpdateData.emit(el.data),
        this.onEditAmountError.bind(this)
      );
    this.someSubscriptions.add(subEditAmount);
  }

  onEditAmountError(error: Error) {
    this.toast.error(
      this.translate.instant('CART.cart_error'),
      this.translate.instant('CART.cant_edit_amount')
    );
  }

  editAmountAll(amount, enableLoading: boolean = true) {
    if (enableLoading) {
      this.loading = true;
    }
    const pack: Donate = { amount };
    const subEditAmountAll = this.cartService.changeAmountAll(pack).subscribe((el) => {
      this.loading = false;
      this.cartService.callUpdateData.emit(el.data);
      setTimeout(() => {
        this.triggerResize();
      });
    }, this.onEditAmountAllError.bind(this));
    this.someSubscriptions.add(subEditAmountAll);
  }

  onEditAmountAllError(error: Error) {
    this.loading = false;
    this.toast.error(
      this.translate.instant('CART.cart_error'),
      this.translate.instant('CART.cant_edit_amount_all')
    );
  }
  triggerResize() {
    const resizeEvent = window.document.createEvent('UIEvents');
    resizeEvent.initUIEvent('resize', true, false, window, 1);
    window.dispatchEvent(resizeEvent);
  }
}
